import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Bot, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { siteConfig, inquiryTypes } from '../config/site';
import { sendEnquiry } from '../lib/sendEnquiry';

const STEPS = ['welcome', 'name', 'email', 'type', 'message', 'sending', 'done'];

const initialMessages = () => [
  {
    id: 'welcome',
    role: 'agent',
    text: `Hello — I'm ${siteConfig.arAgent.name}, your ${siteConfig.arAgent.title}. ${siteConfig.arAgent.tagline}\n\nMay I have your full name?`,
  },
];

const ARAgent = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('name');
  const [messages, setMessages] = useState(initialMessages);
  const [input, setInput] = useState('');
  const [form, setForm] = useState({ name: '', email: '', inquiryType: '', message: '' });
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (open && inputRef.current && step !== 'type' && step !== 'sending' && step !== 'done') {
      inputRef.current.focus();
    }
  }, [open, step, messages]);

  const addAgentMessage = (text) => {
    setMessages((prev) => [...prev, { id: `${Date.now()}-agent`, role: 'agent', text }]);
  };

  const addUserMessage = (text) => {
    setMessages((prev) => [...prev, { id: `${Date.now()}-user`, role: 'user', text }]);
  };

  const resetChat = () => {
    setStep('name');
    setMessages(initialMessages());
    setInput('');
    setForm({ name: '', email: '', inquiryType: '', message: '' });
    setSending(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const submitEnquiry = async (data) => {
    setSending(true);
    setStep('sending');
    addAgentMessage('Thank you. Forwarding your enquiry to our team now…');

    try {
      await sendEnquiry({
        ...data,
        source: 'ar-agent',
      });
      setStep('done');
      addAgentMessage(
        `Your enquiry has been sent. A member of our team will respond to ${data.email} shortly.\n\nIs there anything else I can help with?`
      );
      toast.success('Enquiry sent — our team will be in touch soon.');
    } catch (err) {
      setStep('message');
      const detail = err.message && !err.message.includes('JSON') ? `\n\n(${err.message})` : '';
      addAgentMessage(
        `I wasn't able to send that just now.${detail}\n\nPlease try again, or email us directly at ${siteConfig.enquiryEmail}.`
      );
      toast.error(err.message || 'Failed to send enquiry.');
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const value = input.trim();
    if (!value || sending) return;

    if (step === 'name') {
      if (value.length < 2) {
        toast.error('Please enter your full name.');
        return;
      }
      addUserMessage(value);
      setForm((f) => ({ ...f, name: value }));
      setInput('');
      setStep('email');
      addAgentMessage(`Thank you, ${value.split(' ')[0]}. What email address should we use to reply?`);
      return;
    }

    if (step === 'email') {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        toast.error('Please enter a valid email address.');
        return;
      }
      addUserMessage(value);
      setForm((f) => ({ ...f, email: value }));
      setInput('');
      setStep('type');
      addAgentMessage('What is your enquiry regarding?');
      return;
    }

    if (step === 'message') {
      if (value.length < 10) {
        toast.error('Please provide a few more details (at least 10 characters).');
        return;
      }
      addUserMessage(value);
      const payload = { ...form, message: value };
      setForm(payload);
      setInput('');
      submitEnquiry(payload);
    }
  };

  const handleTypeSelect = (type) => {
    if (sending) return;
    addUserMessage(type.label);
    const updated = { ...form, inquiryType: type.value };
    setForm(updated);
    setStep('message');
    addAgentMessage('Please share the details of your enquiry.');
  };

  const handleNewEnquiry = () => {
    resetChat();
  };

  const showInput = step === 'name' || step === 'email' || step === 'message';
  const inputPlaceholder =
    step === 'name'
      ? 'Your full name'
      : step === 'email'
        ? 'you@company.com'
        : 'Tell us about your enquiry…';

  const inputType = step === 'email' ? 'email' : 'text';

  return (
    <>
      {/* Floating trigger */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-6 right-6 z-[60] flex items-center gap-3 bg-[#0B1F3A] text-white pl-4 pr-5 py-3 shadow-lg hover:bg-[#0d2847] transition-colors group"
            aria-label="Open AR inquiry assistant"
          >
            <span className="flex h-10 w-10 items-center justify-center bg-[#B8941F] text-[#0B1F3A] font-bold text-sm tracking-wider">
              AR
            </span>
            <span className="hidden sm:block text-left">
              <span className="block text-xs uppercase tracking-widest text-[#B8941F]">Inquiry</span>
              <span className="block text-sm font-medium">Ask AR</span>
            </span>
            <MessageCircle size={18} className="text-[#B8941F] sm:hidden" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: 'spring', damping: 26, stiffness: 320 }}
            className="fixed bottom-6 right-6 z-[60] flex w-[calc(100vw-2rem)] max-w-[400px] flex-col overflow-hidden border border-border bg-background shadow-2xl"
            style={{ height: 'min(560px, calc(100vh - 3rem))' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between bg-[#0B1F3A] px-4 py-3 text-white">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center bg-[#B8941F] text-[#0B1F3A] font-bold text-xs">
                  AR
                </span>
                <div>
                  <p className="text-sm font-semibold leading-tight">{siteConfig.arAgent.title}</p>
                  <p className="text-[10px] text-white/60 uppercase tracking-wider">AR Holdings Group</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="rounded p-1.5 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                aria-label="Close assistant"
              >
                <X size={18} />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F8FAFC] dark:bg-muted/20">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  {msg.role === 'agent' && (
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center bg-[#0B1F3A] text-[#B8941F]">
                      <Bot size={14} />
                    </span>
                  )}
                  <div
                    className={`max-w-[85%] px-3 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      msg.role === 'user'
                        ? 'bg-[#0B1F3A] text-white'
                        : 'bg-card border border-border text-foreground'
                    }`}
                  >
                    {msg.text}
                  </div>
                </div>
              ))}

              {step === 'type' && (
                <div className="flex flex-wrap gap-2 pl-9">
                  {inquiryTypes.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => handleTypeSelect(type)}
                      className="border border-[#B8941F] bg-white px-3 py-1.5 text-xs font-medium text-[#0B1F3A] hover:bg-[#B8941F]/10 transition-colors dark:bg-card dark:text-foreground"
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              )}

              {sending && (
                <div className="flex items-center gap-2 pl-9 text-sm text-muted-foreground">
                  <Loader2 size={14} className="animate-spin" />
                  Sending…
                </div>
              )}
            </div>

            {/* Input / actions */}
            <div className="border-t border-border bg-background p-3">
              {step === 'done' ? (
                <div className="flex gap-2">
                  <button
                    onClick={handleNewEnquiry}
                    className="flex-1 bg-[#B8941F] py-2.5 text-sm font-medium text-[#0B1F3A] hover:bg-[#c5a028] transition-colors"
                  >
                    New Enquiry
                  </button>
                  <button
                    onClick={handleClose}
                    className="flex-1 border border-border py-2.5 text-sm font-medium hover:bg-muted transition-colors"
                  >
                    Close
                  </button>
                </div>
              ) : showInput ? (
                <form onSubmit={handleSubmit} className="flex gap-2">
                  <input
                    ref={inputRef}
                    type={inputType}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={inputPlaceholder}
                    disabled={sending}
                    className="flex-1 border border-border bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[#B8941F] disabled:opacity-50"
                  />
                  <button
                    type="submit"
                    disabled={!input.trim() || sending}
                    className="flex h-10 w-10 shrink-0 items-center justify-center bg-[#0B1F3A] text-[#B8941F] hover:bg-[#0d2847] disabled:opacity-40 transition-colors"
                    aria-label="Send"
                  >
                    <Send size={16} />
                  </button>
                </form>
              ) : (
                <p className="text-center text-xs text-muted-foreground py-1">
                  Select an option above to continue
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ARAgent;
